'use client'

import * as React from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { BlogCoverGraphic } from './blog-cover-graphic'

// Category-filtered post grid for the cobalt blog index (Jun 2026
// mockup). Real category chips (derived from the actual articles) filter
// a real post grid client-side; the page stays server-rendered and
// cacheable because every card is passed in as data.
//
// Covers: each post's real cover photo when present, otherwise the
// deterministic BlogCoverGraphic (cobalt/navy brand gradient) — never a
// fake stock photo.

export type GridCard = {
  key: string
  href: string
  title: string
  excerpt: string | null
  cover: string | null
  meta: string | null
  category: string | null
  graphic: { category: string; seed: string } | null
  comingSoon?: boolean
  /** Short stat pill, e.g. "+22% CR" or "84% of shoppers". Shown as a
   *  lime-tinted badge on the card cover so the number catches the eye
   *  before the reader clicks in. */
  keyMetric?: string | null
}

export function BlogCardGrid({
  cards,
  categories,
}: {
  cards: GridCard[]
  categories: string[]
}) {
  const [active, setActive] = React.useState<string>('All')
  const chips = React.useMemo(() => ['All', ...categories], [categories])
  // Incremental rendering: /blog carries 300+ cards, and server-rendering
  // every one (each with its inline cover SVG) pushed the page past
  // Googlebot's ~2 MB render budget (Ahrefs "page size exceeds" error).
  // First paint shows a page of cards; "Show more" reveals the rest.
  const PAGE = 48
  const [limit, setLimit] = React.useState(PAGE)
  const filtered = active === 'All' ? cards : cards.filter((c) => c.category === active)
  const visible = filtered.slice(0, limit)
  const remaining = filtered.length - visible.length

  return (
    <>
      {/* Category chips — cobalt active, hairline idle. Cobalt is the only
          active fill; lime is not used here. */}
      <div className="flex flex-wrap gap-2.5">
        {chips.map((label) => {
          const isActive = active === label
          return (
            <button
              key={label}
              type="button"
              onClick={() => { setActive(label); setLimit(PAGE) }}
              aria-pressed={isActive}
              className={`rounded-full px-4 py-2 text-sm font-semibold outline-none transition focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-reduce:transition-none ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border bg-card text-foreground/70 hover:border-primary/40 hover:text-foreground'
              }`}
            >
              {label}
            </button>
          )
        })}
      </div>

      <ul className="mt-7 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((card) => (
          <li key={card.key}>
            <Link
              href={card.href}
              className="group flex h-full flex-col overflow-hidden rounded-[18px] border border-border bg-card outline-none transition hover:border-primary/40 hover:shadow-md focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-safe:hover:-translate-y-0.5 motion-reduce:transition-none"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                {card.graphic ? (
                  <BlogCoverGraphic
                    title={card.title}
                    category={card.graphic.category}
                    seed={card.graphic.seed}
                    size="md"
                  />
                ) : card.cover ? (
                  <Image
                    src={card.cover}
                    alt={card.title}
                    width={640}
                    height={400}
                    className="h-full w-full object-cover transition group-hover:scale-105 motion-reduce:transition-none"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-[hsl(222_80%_96%)] to-[hsl(216_90%_45%)]" />
                )}
                {card.category && (
                  <span className="absolute left-3 top-3 rounded-full bg-card/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em] text-primary backdrop-blur-sm">
                    {card.category}
                  </span>
                )}
                {card.keyMetric && (
                  <span className="absolute bottom-3 left-3 rounded-full border border-brand-lime/30 bg-brand-navy/90 px-2.5 py-1 text-[11px] font-black tracking-tight text-brand-lime backdrop-blur-sm">
                    {card.keyMetric}
                  </span>
                )}
                {card.comingSoon && (
                  <span className="absolute right-3 top-3 rounded-full bg-card/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-foreground/80 backdrop-blur-sm">
                    Coming soon
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col p-5">
                <h3 className="font-display text-[18px] font-bold leading-snug tracking-tight text-foreground transition group-hover:text-primary">
                  {card.title}
                </h3>
                {card.excerpt && (
                  <p className="mt-2 line-clamp-2 text-sm leading-snug text-muted-foreground">{card.excerpt}</p>
                )}
                {card.meta && <p className="mt-auto pt-4 text-[13px] text-foreground/45">{card.meta}</p>}
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {remaining > 0 && (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setLimit((l) => l + PAGE)}
            className="rounded-full border border-border bg-card px-6 py-2.5 text-sm font-semibold text-foreground outline-none transition hover:border-primary/40 hover:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 motion-safe:hover:-translate-y-0.5 motion-reduce:transition-none"
          >
            Show more articles ({remaining} left)
          </button>
        </div>
      )}

      {visible.length === 0 && (
        <div className="mt-7 rounded-2xl border border-dashed border-border p-12 text-center">
          <p className="text-sm text-muted-foreground">
            No posts in <span className="font-medium text-foreground">{active}</span> yet. Pick another track.
          </p>
        </div>
      )}
    </>
  )
}
